import React, { useState, useEffect, useCallback } from "react";
import {
  Grid, Card, Typography, TextField, Button, Box, MenuItem,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Chip, Divider, CircularProgress,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import DownloadIcon from "@mui/icons-material/Download";
import axios from "axios";
import { useNotifier } from "../../../Core/Notifier";
import { useStyles } from "../../../theme/appStyles";
import dayjs, { Dayjs } from "dayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";

interface Expense {
  id: number;
  date: string;
  description: string;
  amount: string | number;
  hst: string | number;
  total: string | number;
  category: string | number;
  type: string;
  status: string;
  is_installment: boolean;
  paid_amount: string | number;
  due_amount: string | number;
  remarks: string;
}

interface Summary {
  count: number;
  total_amount: number;
  total_hst: number;
  total: number;
  total_paid: number;
  total_unpaid: number;
}

interface Category {
  id: number;
  category_name: string;
}

const TYPES = ["", "Other", "Labour", "Material", "Material + Labour"];
const STATUSES = ["", "Paid", "Unpaid"];

const ExpenseReports = () => {
  const API_URL = process.env.REACT_APP_API_URL;
  const classes = useStyles();
  const { showErrorMessage } = useNotifier();

  const today = new Date().toISOString().split("T")[0];
  const firstOfMonth = new Date(new Date().getFullYear(), new Date().getMonth(), 1)
    .toISOString().split("T")[0];

  const [filters, setFilters] = useState({
    start_date: firstOfMonth,
    end_date: today,
    category: "",
    status: "",
    type: "",
  });
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    axios.get(`${API_URL}/view-expense-category`)
      .then(r => setCategories(r.data || []))
      .catch(() => {});
  }, []);

  const catName = (id: string | number) => {
    const found = categories.find(c => String(c.id) === String(id));
    return found ? found.category_name : `#${id}`;
  };

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const params: any = {};
      if (filters.start_date) params.start_date = filters.start_date;
      if (filters.end_date)   params.end_date   = filters.end_date;
      if (filters.category)   params.category   = filters.category;
      if (filters.status)     params.status     = filters.status;
      if (filters.type)       params.type       = filters.type;

      const response = await axios.get(`${API_URL}/expenses/report`, { params });
      setExpenses(response.data.data || []);
      setSummary(response.data.summary || null);
    } catch {
      showErrorMessage("Failed to load report data.");
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { fetchReport(); }, [filters.start_date, filters.end_date]);

  const handleSearch = () => fetchReport();

  const exportCSV = () => {
    const headers = ["Date", "Description", "Type", "Category", "Amount", "HST", "Total", "Status", "Paid", "Due", "Remarks"];
    const rows = expenses.map(e => [
      e.date,
      `"${(e.description || "").replace(/"/g, '""')}"`,
      e.type || "Other",
      catName(e.category),
      Number(e.amount).toFixed(2),
      Number(e.hst).toFixed(2),
      Number(e.total).toFixed(2),
      e.status,
      Number(e.paid_amount).toFixed(2),
      Number(e.due_amount).toFixed(2),
      `"${(e.remarks || "").replace(/"/g, '""')}"`,
    ]);
    const csv = [headers.join(","), ...rows.map(r => r.join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement("a");
    a.href     = url;
    a.download = `expense_report_${filters.start_date}_to_${filters.end_date}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const statCards = summary ? [
    { label: "Total Expenses",  value: `$${summary.total.toFixed(2)}`,        color: "#2f3891" },
    { label: "Total Paid",      value: `$${summary.total_paid.toFixed(2)}`,   color: "#22c55e" },
    { label: "Total Unpaid",    value: `$${summary.total_unpaid.toFixed(2)}`, color: "#ef4444" },
    { label: "HST Collected",   value: `$${summary.total_hst.toFixed(2)}`,    color: "#f59e0b" },
    { label: "Records",         value: String(summary.count),                 color: "#0ea5e9" },
  ] : [];

  return (
    <Grid container spacing={3}>
      {/* Header */}
      <Grid item xs={12}>
        <Card sx={{ p: 3, background: "linear-gradient(135deg, #2f3891 0%, #2f3891 100%)", color: "#fff", borderRadius: 3 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
            <Box>
              <Typography variant="h5" fontWeight={700}>Expense Reports</Typography>
              <Typography variant="body2" sx={{ opacity: 0.85, mt: 0.5 }}>
                Filter, analyse and export your expense data
              </Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={exportCSV}
              disabled={expenses.length === 0}
              sx={{ bgcolor: "rgba(255,255,255,0.2)", "&:hover": { bgcolor: "rgba(255,255,255,0.3)" }, fontWeight: 600 }}
            >
              Export CSV
            </Button>
          </Box>
        </Card>
      </Grid>

      {/* Filters */}
      <Grid item xs={12}>
        <Card sx={{ p: 3, borderRadius: 3 }}>
          <Typography variant="subtitle1" fontWeight={700} mb={2}>Filter Options</Typography>
          <Grid container spacing={2} alignItems="flex-end">
            <Grid item xs={12} sm={6} md={2}>
              <Typography variant="caption" color="text.secondary">Start Date</Typography>
              <DatePicker
                views={["day"]}
                openTo="day"
                value={filters.start_date ? dayjs(filters.start_date) : null}
                onChange={(val: Dayjs | null) => setFilters(f => ({ ...f, start_date: val && val.isValid() ? val.format("YYYY-MM-DD") : "" }))}
                format="MMM DD, YYYY"
                slotProps={{
                  textField: { size: "small", fullWidth: true, placeholder: "Select date",
                    sx: { "& .MuiOutlinedInput-root": { borderRadius: 2.5, bgcolor: "#f9fafb", fontSize: 13, height: 40 } } },
                  popper: { sx: {
                    "& .MuiPaper-root": { borderRadius: 3, boxShadow: "0 12px 40px rgba(0,0,0,0.12)", border: "1px solid #e2e8f0" },
                    "& .MuiPickersDay-root.Mui-selected": { bgcolor: "#2f3891 !important", fontWeight: 700 },
                    "& .MuiPickersDay-root:hover": { bgcolor: "#eef2ff" },
                    "& .MuiPickersDay-root": { borderRadius: 2, fontWeight: 500 },
                    "& .MuiPickersCalendarHeader-label": { fontWeight: 700, fontSize: 14, color: "#1e293b", pointerEvents: "none" },
                    "& .MuiPickersArrowSwitcher-button": { borderRadius: 1.5 },
                  }},
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Typography variant="caption" color="text.secondary">End Date</Typography>
              <DatePicker
                views={["day"]}
                openTo="day"
                value={filters.end_date ? dayjs(filters.end_date) : null}
                onChange={(val: Dayjs | null) => setFilters(f => ({ ...f, end_date: val && val.isValid() ? val.format("YYYY-MM-DD") : "" }))}
                format="MMM DD, YYYY"
                slotProps={{
                  textField: { size: "small", fullWidth: true, placeholder: "Select date",
                    sx: { "& .MuiOutlinedInput-root": { borderRadius: 2.5, bgcolor: "#f9fafb", fontSize: 13, height: 40 } } },
                  popper: { sx: {
                    "& .MuiPaper-root": { borderRadius: 3, boxShadow: "0 12px 40px rgba(0,0,0,0.12)", border: "1px solid #e2e8f0" },
                    "& .MuiPickersDay-root.Mui-selected": { bgcolor: "#2f3891 !important", fontWeight: 700 },
                    "& .MuiPickersDay-root:hover": { bgcolor: "#eef2ff" },
                    "& .MuiPickersDay-root": { borderRadius: 2, fontWeight: 500 },
                    "& .MuiPickersCalendarHeader-label": { fontWeight: 700, fontSize: 14, color: "#1e293b", pointerEvents: "none" },
                    "& .MuiPickersArrowSwitcher-button": { borderRadius: 1.5 },
                  }},
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Typography variant="caption" color="text.secondary">Category</Typography>
              <TextField select size="small" fullWidth value={filters.category}
                onChange={e => setFilters(f => ({ ...f, category: e.target.value }))}>
                <MenuItem value="">All Categories</MenuItem>
                {categories.map(c => <MenuItem key={c.id} value={c.id}>{c.category_name}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Typography variant="caption" color="text.secondary">Status</Typography>
              <TextField select size="small" fullWidth value={filters.status}
                onChange={e => setFilters(f => ({ ...f, status: e.target.value }))}>
                {STATUSES.map(s => <MenuItem key={s} value={s}>{s || "All Statuses"}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Typography variant="caption" color="text.secondary">Type</Typography>
              <TextField select size="small" fullWidth value={filters.type}
                onChange={e => setFilters(f => ({ ...f, type: e.target.value }))}>
                {TYPES.map(t => <MenuItem key={t} value={t}>{t || "All Types"}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Button
                variant="contained" fullWidth
                startIcon={loading ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : <SearchIcon />}
                onClick={handleSearch} disabled={loading}
                sx={{ bgcolor: "#2f3891", "&:hover": { bgcolor: "#2f3891" }, height: 40 }}
              >
                Search
              </Button>
            </Grid>
          </Grid>
        </Card>
      </Grid>

      {/* Summary Cards */}
      {summary && (
        <Grid item xs={12}>
          <Grid container spacing={2}>
            {statCards.map(s => (
              <Grid item xs={6} sm={4} md key={s.label}>
                <Card sx={{ p: 2.5, borderRadius: 3, borderTop: `4px solid ${s.color}` }}>
                  <Typography variant="h6" fontWeight={700} sx={{ color: s.color }}>{s.value}</Typography>
                  <Typography variant="caption" color="text.secondary">{s.label}</Typography>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Grid>
      )}

      {/* Table */}
      <Grid item xs={12}>
        <Card sx={{ borderRadius: 3, overflow: "hidden" }}>
          <Box sx={{ p: 2.5, borderBottom: "1px solid #f1f5f9" }}>
            <Typography variant="subtitle1" fontWeight={700}>
              Expense Records {expenses.length > 0 && <Chip label={expenses.length} size="small" sx={{ ml: 1, bgcolor: "#eef2ff", color: "#2f3891", fontWeight: 700 }} />}
            </Typography>
          </Box>
          {loading ? (
            <Box display="flex" justifyContent="center" py={6}>
              <CircularProgress sx={{ color: "#2f3891" }} />
            </Box>
          ) : expenses.length === 0 ? (
            <Box textAlign="center" py={6} color="text.secondary">
              <Typography>No expenses found for the selected filters.</Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: "#f8fafc" }}>
                    {["Date", "Description", "Type", "Category", "Amount", "HST", "Total", "Paid", "Due", "Status"].map(h => (
                      <TableCell key={h} sx={{ fontWeight: 700, fontSize: 12, color: "#64748b" }}>{h}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {expenses.map((e, i) => (
                    <TableRow key={e.id} sx={{ bgcolor: i % 2 === 0 ? "#fff" : "#f8fafc", "&:hover": { bgcolor: "#eef2ff" } }}>
                      <TableCell sx={{ fontSize: 12 }}>{e.date}</TableCell>
                      <TableCell sx={{ fontSize: 12, maxWidth: 220 }}>
                        <Typography variant="body2" noWrap title={e.description}>{e.description}</Typography>
                      </TableCell>
                      <TableCell sx={{ fontSize: 12 }}>{e.type || "Other"}</TableCell>
                      <TableCell sx={{ fontSize: 12 }}>{catName(e.category)}</TableCell>
                      <TableCell sx={{ fontSize: 12 }}>${Number(e.amount).toFixed(2)}</TableCell>
                      <TableCell sx={{ fontSize: 12 }}>${Number(e.hst).toFixed(2)}</TableCell>
                      <TableCell sx={{ fontSize: 12, fontWeight: 600 }}>${Number(e.total).toFixed(2)}</TableCell>
                      <TableCell sx={{ fontSize: 12, color: "#22c55e" }}>${Number(e.paid_amount).toFixed(2)}</TableCell>
                      <TableCell sx={{ fontSize: 12, color: Number(e.due_amount) > 0 ? "#ef4444" : "#22c55e" }}>${Number(e.due_amount).toFixed(2)}</TableCell>
                      <TableCell>
                        <Chip
                          label={e.status}
                          size="small"
                          sx={{
                            bgcolor: e.status === "Paid" ? "#dcfce7" : "#fee2e2",
                            color: e.status === "Paid" ? "#166534" : "#991b1b",
                            fontWeight: 600, fontSize: 11,
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {/* Totals footer */}
          {summary && expenses.length > 0 && (
            <>
              <Divider />
              <Box sx={{ p: 2, bgcolor: "#f8fafc", display: "flex", gap: 4, flexWrap: "wrap" }}>
                <Typography variant="body2" fontWeight={700}>
                  Subtotal: <span style={{ color: "#2f3891" }}>${summary.total_amount.toFixed(2)}</span>
                </Typography>
                <Typography variant="body2" fontWeight={700}>
                  HST: <span style={{ color: "#f59e0b" }}>${summary.total_hst.toFixed(2)}</span>
                </Typography>
                <Typography variant="body2" fontWeight={700}>
                  Grand Total: <span style={{ color: "#2f3891" }}>${summary.total.toFixed(2)}</span>
                </Typography>
                <Typography variant="body2" fontWeight={700}>
                  Paid: <span style={{ color: "#22c55e" }}>${summary.total_paid.toFixed(2)}</span>
                </Typography>
                <Typography variant="body2" fontWeight={700}>
                  Unpaid: <span style={{ color: "#ef4444" }}>${summary.total_unpaid.toFixed(2)}</span>
                </Typography>
              </Box>
            </>
          )}
        </Card>
      </Grid>
    </Grid>
  );
};

export default ExpenseReports;
